import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './ShipstationOrderDetail.css';

const ShipstationOrderDetail = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { orderId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://gokulnair.com/new_project/api/shipstation/order/${orderId}`);
      
      if (response.data.status && response.data.data) {
        setOrder(response.data.data);
      } else {
        setError('Invalid response format from API');
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'shipped':
        return 'status-shipped';
      case 'awaiting_shipment':
        return 'status-awaiting';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };

  const getPaymentMethodClass = (method) => {
    switch (method?.toLowerCase()) {
      case 'stripe':
        return 'payment-stripe';
      case 'paypal':
        return 'payment-paypal';
      case 'offline':
        return 'payment-offline';
      default:
        return 'payment-unknown';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading order details...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="no-order">Order not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="order-detail-content">
        <div className="order-header">
          <button 
            className="back-button"
            onClick={() => navigate('/shipstation-orders')}
          >
            ← Back to Orders
          </button>
          <h1>Order Details</h1>
        </div>

        <div className="order-overview">
          <div className="order-basic-info">
            <div className="info-card">
              <h3>Order Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Order ID:</label>
                  <span className="order-id">{order.orderId}</span>
                </div>
                <div className="info-item">
                  <label>Order Number:</label>
                  <span>{order.orderNumber}</span>
                </div>
                <div className="info-item">
                  <label>Order Key:</label>
                  <span className="order-key">{order.orderKey}</span>
                </div>
                <div className="info-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusBadgeClass(order.orderStatus)}`}>
                    {order.orderStatus?.replace('_', ' ')}
                  </span>
                </div>
                <div className="info-item">
                  <label>Order Date:</label>
                  <span>{formatDate(order.orderDate)}</span>
                </div>
                <div className="info-item">
                  <label>Create Date:</label>
                  <span>{formatDate(order.createDate)}</span>
                </div>
                <div className="info-item">
                  <label>Modify Date:</label>
                  <span>{formatDate(order.modifyDate)}</span>
                </div>
                <div className="info-item">
                  <label>Payment Date:</label>
                  <span>{formatDate(order.paymentDate)}</span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>Customer Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Customer ID:</label>
                  <span>{order.customerId}</span>
                </div>
                <div className="info-item">
                  <label>Username:</label>
                  <span>{order.customerUsername}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{order.customerEmail}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="order-financial-info">
            <div className="info-card">
              <h3>Financial Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Order Total:</label>
                  <span className="total-amount">{formatCurrency(order.orderTotal)}</span>
                </div>
                <div className="info-item">
                  <label>Amount Paid:</label>
                  <span className="paid-amount">{formatCurrency(order.amountPaid)}</span>
                </div>
                <div className="info-item">
                  <label>Tax Amount:</label>
                  <span>{formatCurrency(order.taxAmount)}</span>
                </div>
                <div className="info-item">
                  <label>Shipping Amount:</label>
                  <span>{formatCurrency(order.shippingAmount)}</span>
                </div>
                <div className="info-item">
                  <label>Payment Method:</label>
                  <span className={`payment-badge ${getPaymentMethodClass(order.paymentMethod)}`}>
                    {order.paymentMethod || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="addresses-section">
          <div className="info-card">
            <h3>Billing Address</h3>
            <div className="address-info">
              <div className="address-name">{order.billTo?.name || 'N/A'}</div>
              <div className="address-company">{order.billTo?.company || ''}</div>
              <div className="address-street">{order.billTo?.street1 || 'N/A'}</div>
              {order.billTo?.street2 && <div className="address-street">{order.billTo.street2}</div>}
              {order.billTo?.street3 && <div className="address-street">{order.billTo.street3}</div>}
              <div className="address-city">
                {order.billTo?.city && `${order.billTo.city}, `}
                {order.billTo?.state && `${order.billTo.state} `}
                {order.billTo?.postalCode}
              </div>
              <div className="address-country">{order.billTo?.country || 'N/A'}</div>
              <div className="address-phone">{order.billTo?.phone || 'N/A'}</div>
            </div>
          </div>

          <div className="info-card">
            <h3>Shipping Address</h3>
            <div className="address-info">
              <div className="address-name">{order.shipTo?.name || 'N/A'}</div>
              <div className="address-company">{order.shipTo?.company || ''}</div>
              <div className="address-street">{order.shipTo?.street1 || 'N/A'}</div>
              {order.shipTo?.street2 && <div className="address-street">{order.shipTo.street2}</div>}
              {order.shipTo?.street3 && <div className="address-street">{order.shipTo.street3}</div>}
              <div className="address-city">
                {order.shipTo?.city && `${order.shipTo.city}, `}
                {order.shipTo?.state && `${order.shipTo.state} `}
                {order.shipTo?.postalCode}
              </div>
              <div className="address-country">{order.shipTo?.country || 'N/A'}</div>
              <div className="address-phone">{order.shipTo?.phone || 'N/A'}</div>
              <div className="address-verified">
                Verified: {order.shipTo?.addressVerified || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        <div className="items-section">
          <div className="info-card">
            <h3>Order Items</h3>
            <div className="items-table">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>SKU</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items?.map((item, index) => (
                    <tr key={item.orderItemId || index}>
                      <td>
                        <div className="item-info">
                          <div className="item-name">{item.name}</div>
                          {item.imageUrl && (
                            <img 
                              src={item.imageUrl} 
                              alt={item.name}
                              className="item-image"
                            />
                          )}
                        </div>
                      </td>
                      <td>{item.sku || 'N/A'}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unitPrice)}</td>
                      <td>{formatCurrency(item.unitPrice * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="shipping-section">
          <div className="info-card">
            <h3>Shipping Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Requested Service:</label>
                <span>{order.requestedShippingService || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Carrier Code:</label>
                <span>{order.carrierCode || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Service Code:</label>
                <span>{order.serviceCode || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Package Code:</label>
                <span>{order.packageCode || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Confirmation:</label>
                <span>{order.confirmation || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Ship Date:</label>
                <span>{formatDate(order.shipDate)}</span>
              </div>
              <div className="info-item">
                <label>Weight:</label>
                <span>
                  {order.weight?.value} {order.weight?.units}
                </span>
              </div>
            </div>
          </div>
        </div>

        {order.customerNotes && (
          <div className="notes-section">
            <div className="info-card">
              <h3>Customer Notes</h3>
              <p>{order.customerNotes}</p>
            </div>
          </div>
        )}

        {order.internalNotes && (
          <div className="notes-section">
            <div className="info-card">
              <h3>Internal Notes</h3>
              <p>{order.internalNotes}</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ShipstationOrderDetail; 